// ============================================================
// usePermissions - Permission listener + isLocked helper
// ============================================================
// Extracted from useFirestoreSync. Handles:
//   - Permission listener via mp.onPermissionsUpdate
//   - isLocked(feature) with player override + global lock + defaults
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import { DEFAULT_LOCKS } from '../engine/constants';
import type * as mpModule from '../multiplayer';
import { overridesFor } from '../engine/settings/overrides';
import type { PermissionsDoc } from '../engine/settings/permissions';

interface UsePermissionsParams {
  sessionId: string | null;
  isGm: boolean;
  playerId: string | null;
  /** Multiplayer service (DI) */
  mp: typeof mpModule;
}

export interface UsePermissionsReturn {
  permissions: PermissionsDoc | null;
  isLocked: (feature: string) => boolean;
}

/**
 * Whether a feature is locked for this player: their own override first,
 * then the table-wide lock, then the default.
 *
 * The override is the seat's ACCOUNT's (see `engine/settings/overrides.ts`),
 * and for `waitingRoom` this order is mirrored by `hasWaitingGrant` in
 * firestore.rules and by pb/pb_hooks/waiting.js - the server answers
 * the same question, or the page offers what the server refuses.
 */
function lockedFor(permissions: PermissionsDoc | null, playerId: string | null, feature: string): boolean {
  const override = playerId ? overridesFor(permissions, playerId) : undefined;
  if (override?.[feature] !== undefined) return override[feature];
  if (permissions?.locks?.[feature] !== undefined) return permissions.locks[feature];
  return (DEFAULT_LOCKS as Record<string, boolean>)[feature] || false;
}

export default function usePermissions({ sessionId, isGm, playerId, mp }: UsePermissionsParams): UsePermissionsReturn {
  const [permissions, setPermissionsState] = useState<PermissionsDoc | null>(null);

  /** Nothing is locked outside a session, or for the GM. */
  const isLocked = useCallback(
    (feature: string) => (!sessionId || isGm ? false : lockedFor(permissions, playerId, feature)),
    [sessionId, isGm, playerId, permissions],
  );

  // ---- Permissions listener ----
  // Resubscription-on-error lives in multiplayer.ts (resilientListener
  // wraps every session listener) - the hook stays a plain subscription.
  useEffect(() => {
    if (!sessionId) return;
    return listenLive(mp, sessionId, setPermissionsState);
  }, [sessionId, mp]);

  return { permissions, isLocked };
}

/**
 * Subscribe, and IGNORE A CALLBACK FROM A LISTENER WE HAVE ALREADY TORN
 * DOWN.
 *
 * StrictMode mounts every effect twice - subscribe, clean up, subscribe
 * again - and unsubscribing does not un-queue a snapshot the SDK has
 * already dispatched. So the FIRST listener can still deliver, after the
 * second one has delivered the real document, and what it delivers is the
 * cached `exists: false` from before the document was read.
 * `onPermissionsUpdate` turns that into `callback(null)`, `null` means
 * DEFAULT_LOCKS downstream, and the GM's lock silently stops applying to
 * the player. Measured at about one run in six of `turn-tracker.spec.js` ›
 * Permissions (v8.97.0), and present on `main`.
 *
 * The flag is the fix rather than de-duplicating the subscription: a late
 * callback from a dead listener is wrong whatever caused it.
 */
function listenLive(mp: typeof mpModule, sessionId: string, set: (perms: PermissionsDoc | null) => void) {
  let live = true;
  const unsub = mp.onPermissionsUpdate(sessionId, (perms) => {
    if (live) set(perms);
  });
  return () => {
    live = false;
    unsub();
  };
}
